import { httpsCallable } from "firebase/functions";

export const MUX_DIAGNOSTIC_EVENTS = Object.freeze([
  "MUX_PROVIDER_SELECTED",
  "MUX_CREDENTIAL_REQUEST_START",
  "MUX_CREDENTIAL_REQUEST_OK",
  "MUX_CREDENTIAL_REQUEST_FAILED",
  "MUX_HLS_ENGINE_SELECTED",
  "MUX_NATIVE_HLS_ENGINE_SELECTED",
  "MUX_MEDIA_ATTACHED",
  "MUX_MANIFEST_LOADING",
  "MUX_MANIFEST_LOADED",
  "MUX_LEVEL_LOADED",
  "MUX_FIRST_FRAGMENT_LOADED",
  "MUX_FIRST_BUFFER_APPENDED",
  "MUX_LOADED_METADATA",
  "MUX_FIRST_PLAY",
  "MUX_PLAYING",
  "MUX_FATAL_ERROR",
]);

const EVENT_SET = new Set(MUX_DIAGNOSTIC_EVENTS);
const FIRST_ONLY = new Set([
  "MUX_MEDIA_ATTACHED", "MUX_MANIFEST_LOADING", "MUX_MANIFEST_LOADED",
  "MUX_LEVEL_LOADED", "MUX_FIRST_FRAGMENT_LOADED", "MUX_FIRST_BUFFER_APPENDED",
  "MUX_LOADED_METADATA", "MUX_FIRST_PLAY", "MUX_PLAYING", "MUX_FATAL_ERROR",
]);
const MAX_EVENTS = 18;
const MAX_TEXT = 120;
const DETAIL_TEXT_FIELDS = Object.freeze({
  playbackEngine: 20, credentialSource: 20, hlsErrorType: 40, hlsErrorDetails: 80,
  contextType: 40, reason: 160, errorName: 80, mimeType: 120, sourceBufferName: 20,
  hostname: 100, requestClass: 30, videoCodec: 80, audioCodec: 80,
  videoMimeType: 120, audioMimeType: 120, failureStage: 50,
});
const DETAIL_BOOLEAN_FIELDS = new Set([
  "fatal", "hlsSupported", "mediaSourceSupported",
  "videoCodecSupported", "audioCodecSupported",
]);
const DETAIL_NUMBER_FIELDS = Object.freeze({
  httpStatus: [0, 599], mediaErrorCode: [0, 9], bytesLoaded: [0, 1_000_000_000],
  loadRetryCount: [0, 100],
});

function boundedText(value, max = MAX_TEXT) {
  if (typeof value !== "string") return undefined;
  const text = value.trim().slice(0, max);
  if (!text) return undefined;
  if (/https?:\/\//i.test(text) || /(?:token|authorization|cookie)\s*[=:]/i.test(text) ||
      /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/.test(text)) {
    return "redacted";
  }
  return text;
}

function boundedNumber(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  return Math.min(max, Math.max(min, number));
}

export function classifyMuxRequest({ contextType, details, url } = {}) {
  const type = String(contextType || "");
  const detail = String(details || "").toLowerCase();
  if (type === "manifest" || detail.startsWith("manifest")) return "master-manifest";
  if (["level", "audioTrack", "subtitleTrack"].includes(type) ||
      /level|playlist|audio.?track|subtitle/.test(detail)) return "level-playlist";
  if (type === "media-fragment" || /frag/.test(detail)) return "media-fragment";
  if (type === "key" || /keyload/.test(detail)) return "encryption-key";
  try {
    const pathname = new URL(String(url || "")).pathname.toLowerCase();
    if (pathname.endsWith(".m3u8")) return "level-playlist";
  } catch {}
  return "other";
}

export function sanitizeMuxRequestUrl(url, hints = {}) {
  try {
    const parsed = new URL(String(url || ""));
    return {
      hostname: boundedText(parsed.hostname.toLowerCase(), 100),
      requestClass: classifyMuxRequest({ ...hints, url }),
    };
  } catch {
    return { requestClass: classifyMuxRequest(hints) };
  }
}

function coarseBrowserContext(navigatorValue = navigator) {
  const ua = String(navigatorValue?.userAgent || "");
  const match = ua.match(/(?:Edg|OPR|Chrome|CriOS|Firefox|FxiOS|Version)\/(\d+)/i);
  let browserFamily = "other";
  if (/Edg\//.test(ua)) browserFamily = "edge";
  else if (/OPR\//.test(ua)) browserFamily = "opera";
  else if (/(?:Chrome|CriOS)\//.test(ua)) browserFamily = "chrome";
  else if (/(?:Firefox|FxiOS)\//.test(ua)) browserFamily = "firefox";
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browserFamily = "safari";
  let osFamily = "other";
  if (/Windows/i.test(ua)) osFamily = "windows";
  else if (/(?:iPhone|iPad|iPod)/i.test(ua)) osFamily = "ios";
  else if (/Android/i.test(ua)) osFamily = "android";
  else if (/Mac OS X/i.test(ua)) osFamily = "macos";
  else if (/Linux/i.test(ua)) osFamily = "linux";
  return {
    browserFamily,
    ...(match ? { browserVersionMajor: Number(match[1]) } : {}),
    osFamily,
    deviceClass: /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? "mobile" : "desktop",
    online: navigatorValue?.onLine !== false,
  };
}

export function loadedApplicationVersion(documentValue = document) {
  try {
    const scripts = Array.from(documentValue?.scripts || []);
    const src = scripts.map((script) => String(script?.src || ""))
      .find((value) => /\/static\/js\/main\.[^/]+\.js(?:$|\?)/.test(value));
    const match = src?.match(/\/static\/js\/(main\.[^/?]+\.js)/);
    return boundedText(match?.[1] || "development", 80);
  } catch { return "unknown"; }
}

function coarseCacheContext(documentValue = document, performanceValue = window.performance,
  navigatorValue = navigator) {
  try {
    const navigation = performanceValue?.getEntriesByType?.("navigation")?.[0];
    const mainScript = Array.from(documentValue?.scripts || []).map((script) => String(script?.src || ""))
      .find((value) => /\/static\/js\/main\.[^/]+\.js(?:$|\?)/.test(value));
    const resource = mainScript
      ? performanceValue?.getEntriesByName?.(mainScript)?.[0] : undefined;
    let applicationBundleCacheState = "unknown";
    if (resource && Number(resource.encodedBodySize || 0) > 0) {
      applicationBundleCacheState = Number(resource.transferSize || 0) === 0
        ? "memory-or-disk-cache" : "network-or-revalidated";
    }
    return {
      navigationType: boundedText(navigation?.type, 20) || "unknown",
      applicationBundleCacheState,
      serviceWorkerControlled: !!navigatorValue?.serviceWorker?.controller,
    };
  } catch {
    return {
      navigationType: "unknown",
      applicationBundleCacheState: "unknown",
      serviceWorkerControlled: false,
    };
  }
}

export function sanitizeHlsFatalError(data = {}) {
  const url = data?.context?.url || data?.url || data?.frag?.url || "";
  const network = sanitizeMuxRequestUrl(url, {
    contextType: data?.context?.type,
    details: data?.details,
  });
  return compact({
    hlsErrorType: boundedText(data?.type, 40),
    hlsErrorDetails: boundedText(data?.details, 80),
    fatal: data?.fatal === true,
    httpStatus: boundedNumber(data?.response?.code, 0, 599),
    contextType: boundedText(data?.context?.type, 40),
    reason: boundedText(data?.reason, 160),
    errorName: boundedText(data?.error?.name, 80),
    mimeType: boundedText(data?.mimeType, 120),
    sourceBufferName: boundedText(data?.sourceBufferName, 20),
    bytesLoaded: boundedNumber(data?.stats?.loaded, 0, 1_000_000_000),
    loadRetryCount: boundedNumber(data?.stats?.retry, 0, 100),
    ...network,
  });
}

function mediaState(video) {
  return compact({
    mediaErrorCode: boundedNumber(video?.error?.code, 0, 9),
    readyState: boundedNumber(video?.readyState, 0, 4),
    networkState: boundedNumber(video?.networkState, 0, 3),
    currentTime: boundedNumber(video?.currentTime, 0, 86_400),
    visibilityState: boundedText(document?.visibilityState, 20),
  });
}

function compact(value) {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined));
}

function sanitizeEventDetails(details) {
  if (!details || typeof details !== "object" || Array.isArray(details)) return {};
  const result = {};
  for (const [key, max] of Object.entries(DETAIL_TEXT_FIELDS)) {
    const value = boundedText(details[key], max);
    if (value !== undefined) result[key] = value;
  }
  for (const key of DETAIL_BOOLEAN_FIELDS) {
    if (typeof details[key] === "boolean") result[key] = details[key];
  }
  for (const [key, range] of Object.entries(DETAIL_NUMBER_FIELDS)) {
    const value = boundedNumber(details[key], range[0], range[1]);
    if (value !== undefined && details[key] !== undefined) result[key] = value;
  }
  return result;
}

export function codecDiagnostics(levels = [], mediaSource = window.MediaSource) {
  const level = Array.isArray(levels) ? levels.find((item) => item?.videoCodec || item?.audioCodec) : null;
  if (!level) return {};
  const videoCodec = boundedText(level.videoCodec, 80);
  const audioCodec = boundedText(level.audioCodec, 80);
  const supported = (mime) => {
    try { return typeof mediaSource?.isTypeSupported === "function"
      ? mediaSource.isTypeSupported(mime) : undefined; } catch { return undefined; }
  };
  const videoMimeType = videoCodec ? `video/mp4; codecs="${videoCodec}"` : undefined;
  const audioMimeType = audioCodec ? `audio/mp4; codecs="${audioCodec}"` : undefined;
  return compact({ videoCodec, audioCodec, videoMimeType, audioMimeType,
    videoCodecSupported: videoMimeType ? supported(videoMimeType) : undefined,
    audioCodecSupported: audioMimeType ? supported(audioMimeType) : undefined });
}

export function createMuxDiagnosticAttempt({ functions, courseId, headerId, sectionId,
  lessonId, preview = false, transport, navigatorValue, documentValue, performanceValue } = {}) {
  const correlationId = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID() : `mux-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  const events = [];
  const emitted = new Set();
  let flushed = false;
  const send = transport || ((payload) => httpsCallable(functions, "logMuxPlaybackDiagnostic")(payload));
  const base = {
    correlationId, provider: "mux", courseId: boundedText(courseId, 160),
    headerId: boundedText(headerId, 160), sectionId: boundedText(sectionId, 160),
    lessonId: boundedText(lessonId, 160), preview: preview === true,
    applicationVersion: loadedApplicationVersion(documentValue),
    ...coarseBrowserContext(navigatorValue),
    ...coarseCacheContext(documentValue, performanceValue, navigatorValue),
  };
  return {
    correlationId,
    record(event, details = {}, video) {
      try {
        if (!EVENT_SET.has(event) || events.length >= MAX_EVENTS || (FIRST_ONLY.has(event) && emitted.has(event))) return;
        if (FIRST_ONLY.has(event)) emitted.add(event);
        events.push(compact({ event, timestamp: new Date().toISOString(),
          ...sanitizeEventDetails(details), ...mediaState(video) }));
      } catch {}
    },
    flush(outcome) {
      try {
        if (flushed || !events.length) return;
        flushed = true;
        const payload = { ...base, outcome: boundedText(outcome, 40), events: events.slice(0, MAX_EVENTS) };
        Promise.resolve().then(() => send(payload)).catch(() => undefined);
      } catch {}
    },
    snapshot() { return { ...base, events: events.map((event) => ({ ...event })) }; },
  };
}

export function safeMuxDiagnostic(attempt, event, details, video) {
  try { attempt?.record?.(event, details, video); } catch {}
}

export function safeFlushMuxDiagnostic(attempt, outcome) {
  try { attempt?.flush?.(outcome); } catch {}
}
